import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, ModalBody } from "reactstrap";
import { getBookingDetails } from "../../../../actions/admin/scheduled-event-actions";
import { sendPostRequest } from "../../../../actions/app-actions";
import Loader from "../../../../components/Loader/Loader";
import { notification } from "../../../../utils/utility";
import { formatBookingDate } from "./formatBookingDate";
import UpcomingCancelModal from "./upcoming-cancel-modal";
import UpcomingRescheduleModal from "./upcoming-reschedule-modal";
import "./scheduledEvents.scss";

const DetailItem = ({ label, value, wide = false }) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  return (
    <div className={`se-details__item${wide ? " se-details__item--wide" : ""}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
};

const UpcomingViewDetailsModal = (props) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const { getBookingDetails, bookingId } = props;
    setIsLoading(true);
    getBookingDetails({
      data: {
        id: bookingId,
      },
      onSuccess: function (response) {
        setData(response);
        setIsLoading(false);
      },
      onError: function () {
        setIsLoading(false);
      },
    });
  }, []);

  const hideCancelModal = () => {
    setShowCancelModal(false);
  };

  const hideRescheduleModal = () => {
    setShowRescheduleModal(false);
  };

  const onConfirmBooking = () => {
    const { sendPostRequest, handleClose, loadUpcomingEvents } = props;
    if (!data?.id) {
      return;
    }
    setIsLoading(true);
    sendPostRequest({
      url: `/booking/confirm`,
      data: {
        id: data.id,
      },
      onSuccess: function () {
        setIsLoading(false);
        setShowConfirmModal(false);
        handleClose();
        if (loadUpcomingEvents) {
          loadUpcomingEvents();
        }
        notification.success("Booking successfully confirmed");
      },
      onError: function () {
        setIsLoading(false);
        setShowConfirmModal(false);
        notification.error("Could not confirm this booking. Please try again.");
      },
    });
  };

  const isHost = data && data.is_admin_invitee === "D";
  const questions = data?.question_details || [];
  const whenLabel = data
    ? [
        formatBookingDate(data.when, "DD-MM-YYYY"),
        data.start_time && data.end_time
          ? `${data.start_time} – ${data.end_time}`
          : null,
        data.host_timezone ? `(${data.host_timezone})` : null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <>
      <Loader isShowLoader={isLoading} />
      <Modal
        returnFocusAfterClose
        isOpen={props.showViewDetailModal && !isLoading}
        toggle={props.handleClose}
        centered
        className="se-details-modal"
        contentClassName="se-details-modal__content"
      >
        <ModalBody>
          {data ? (
            <div className="se-details">
              <header className="se-details__header">
                <div className="se-details__header-copy">
                  <p className="se-details__eyebrow">Booking details</p>
                  <h2>{data.event_name}</h2>
                  {data.start_time && data.end_time ? (
                    <span className="se-details__slot">
                      {data.start_time} – {data.end_time}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="se-details__close"
                  onClick={props.handleClose}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>

              <dl className="se-details__grid">
                <DetailItem label="When" value={whenLabel} wide />
                <DetailItem label="Host" value={data.host} />
                <DetailItem label="Invitee" value={data.invitee} />
                <DetailItem label="Duration" value={data.event_duration} />
                <DetailItem label="Invitee time zone" value={data.invitee_timezone} />
                <DetailItem label="Venue" value={data.venue} />
                <DetailItem label="Venue details" value={data.venue_details} />
                <DetailItem
                  label="Invitee instructions"
                  value={data.customer_instruction}
                  wide
                />
                <DetailItem
                  label="Invitee feedback"
                  value={data.message_from_customer}
                  wide
                />
                {questions.map((questionDetail, index) => (
                  <DetailItem
                    key={`questions-${index}`}
                    label={questionDetail.question || "Question"}
                    value={questionDetail.answer}
                    wide
                  />
                ))}
              </dl>

              <div className="se-details__actions">
                {data.booking_status === "P" && isHost ? (
                  <button
                    type="button"
                    className="se-btn se-btn--primary"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    Confirm
                  </button>
                ) : null}
                {isHost ? (
                  <button
                    type="button"
                    className="se-btn se-btn--ghost"
                    onClick={() => setShowRescheduleModal(true)}
                  >
                    Reschedule
                  </button>
                ) : null}
                {isHost ? (
                  <button
                    type="button"
                    className="se-btn se-btn--ghost"
                    onClick={() => {
                      navigate(
                        `/admin/events/${data.event_id}/create?type=${
                          data.is_event_paid === "E"
                            ? "paid-event"
                            : "free-event"
                        }`
                      );
                    }}
                  >
                    Edit event
                  </button>
                ) : null}
                {data.booking_status !== "RITR" && isHost ? (
                  <button
                    type="button"
                    className="se-btn se-btn--ghost"
                    onClick={() => {
                      if (typeof props.onRequestInviteeToReschedule === "function") {
                        props.onRequestInviteeToReschedule(data.id);
                      }
                    }}
                  >
                    Ask invitee to reschedule
                  </button>
                ) : null}
                {data.booking_status !== "CC" && isHost ? (
                  <button
                    type="button"
                    className="se-btn se-btn--danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel booking
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </ModalBody>
      </Modal>
      {showCancelModal && (
        <UpcomingCancelModal
          showCancelModal={showCancelModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideCancelModal}
          data={data}
          isBulk={false}
          isStaffCancellation={false}
          loadUpcomingEvents={props.loadUpcomingEvents}
        />
      )}
      {showRescheduleModal && (
        <UpcomingRescheduleModal
          showRescheduleModal={showRescheduleModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideRescheduleModal}
          data={data}
          loadUpcomingEvents={props.loadUpcomingEvents}
        />
      )}
      {showConfirmModal && (
        <Modal
          isOpen={showConfirmModal}
          toggle={() => setShowConfirmModal(false)}
          centered
          className="se-details-modal"
          contentClassName="se-details-modal__content"
        >
          <ModalBody>
            <div className="se-details">
              <header className="se-details__header">
                <div className="se-details__header-copy">
                  <p className="se-details__eyebrow">Confirm</p>
                  <h2>Confirm this booking?</h2>
                </div>
                <button
                  type="button"
                  className="se-details__close"
                  onClick={() => setShowConfirmModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>
              <p className="se-confirm__copy">
                The invitee will be notified that this booking is confirmed.
              </p>
              <div className="se-details__actions">
                <button
                  type="button"
                  className="se-btn se-btn--ghost"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Not now
                </button>
                <button
                  type="button"
                  className="se-btn se-btn--primary"
                  onClick={onConfirmBooking}
                >
                  Confirm
                </button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default connect(null, {
  getBookingDetails,
  sendPostRequest,
})(UpcomingViewDetailsModal);
